import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import { playClickSound, playTabSound } from './utils/sounds';

// Block Rivals piece definitions (all 21 pieces)
const PIECES = {
  I1: [[1]],
  I2: [[1, 1]],
  I3: [[1, 1, 1]],
  I4: [[1, 1, 1, 1]],
  I5: [[1, 1, 1, 1, 1]],
  L4: [[1, 0], [1, 0], [1, 1]],
  L5: [[1, 0], [1, 0], [1, 0], [1, 1]],
  T4: [[1, 1, 1], [0, 1, 0]],
  T5: [[1, 1, 1], [0, 1, 0], [0, 1, 0]],
  O: [[1, 1], [1, 1]],
  V3: [[1, 0], [1, 1]],
  V5: [[1, 0, 0], [1, 0, 0], [1, 1, 1]],
  Z4: [[1, 1, 0], [0, 1, 1]],
  Z5: [[0, 0, 1], [1, 1, 1], [1, 0, 0]],
  P: [[1, 1], [1, 1], [1, 0]],
  W: [[1, 0, 0], [1, 1, 0], [0, 1, 1]],
  U: [[1, 0, 1], [1, 1, 1]],
  F: [[0, 1, 1], [1, 1, 0], [0, 1, 0]],
  X: [[0, 1, 0], [1, 1, 1], [0, 1, 0]],
  Y: [[0, 1], [1, 1], [0, 1], [0, 1]],
  N: [[0, 1], [1, 1], [1, 0], [1, 0]]
};

// Sound generation functions
const playSound = (frequency, duration, type = 'sine', volume = 0.3) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.frequency.value = frequency;
  oscillator.type = type;

  gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

const playPlaceSound = () => {
  // Satisfying ascending chord progression
  playSound(523.25, 0.12, 'sine', 0.25); // C5
  setTimeout(() => playSound(659.25, 0.12, 'sine', 0.25), 40); // E5
  setTimeout(() => playSound(783.99, 0.15, 'sine', 0.2), 80); // G5
  // Add a subtle bass note for depth
  setTimeout(() => playSound(261.63, 0.18, 'triangle', 0.15), 0); // C4
};

const playRotateSound = () => {
  // Quick rising chirp
  playSound(440, 0.08, 'square', 0.2);
  setTimeout(() => playSound(554.37, 0.06, 'square', 0.15), 35);
};

const playFlipSound = () => {
  // Descending then ascending sound for flip motion
  playSound(659.25, 0.06, 'triangle', 0.2); // E5
  setTimeout(() => playSound(523.25, 0.06, 'triangle', 0.2), 40); // C5
  setTimeout(() => playSound(659.25, 0.08, 'triangle', 0.15), 80); // E5
};

const playUndoSound = () => {
  // Descending whoosh
  playSound(698.46, 0.08, 'sawtooth', 0.18); // F5
  setTimeout(() => playSound(523.25, 0.1, 'sawtooth', 0.15), 45); // C5
  setTimeout(() => playSound(392, 0.12, 'sawtooth', 0.12), 85); // G4
};

const playPassSound = () => {
  // Soft declining notes
  playSound(523.25, 0.15, 'sine', 0.2); // C5
  setTimeout(() => playSound(392, 0.15, 'sine', 0.18), 70); // G4
  setTimeout(() => playSound(329.63, 0.2, 'sine', 0.15), 140); // E4
};

const playSelectSound = () => {
  // Quick bright pop
  playSound(880, 0.05, 'sine', 0.22); // A5
  setTimeout(() => playSound(1046.5, 0.04, 'sine', 0.18), 25); // C6
};

const playErrorSound = () => {
  // Dissonant buzz
  playSound(220, 0.12, 'sawtooth', 0.25); // A3
  setTimeout(() => playSound(233.08, 0.12, 'sawtooth', 0.25), 60); // A#3
};

// Utility functions
const rotatePiece = (piece) => {
  const rows = piece.length;
  const cols = piece[0].length;
  const rotated = Array(cols).fill(0).map(() => Array(rows).fill(0));

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      rotated[c][rows - 1 - r] = piece[r][c];
    }
  }
  return rotated;
};

const flipPiece = (piece) => {
  return piece.map(row => [...row].reverse());
};

const initializeBoard = () => {
  return Array(14).fill(0).map(() => Array(14).fill(null));
};

const initializePieces = () => {
  return Object.keys(PIECES).map(id => ({
    id,
    shape: PIECES[id],
    used: false
  }));
};

function App({ onReturnToMenu, initialViewMode = 'desktop', initialColor = 'blue', playerName = 'Player' }) {
  const [board, setBoard] = useState(initializeBoard());
  const [player1Pieces, setPlayer1Pieces] = useState(initializePieces());
  const [player2Pieces, setPlayer2Pieces] = useState(initializePieces());
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [pieceTransform, setPieceTransform] = useState({ rotation: 0, flipped: false });
  const [pendingMove, setPendingMove] = useState(null);
  const [legalPositions, setLegalPositions] = useState([]);
  const [score, setScore] = useState({ player1: 0, player2: 0 });
  const [timer, setTimer] = useState(30);
  const [gameStarted, setGameStarted] = useState(true);
  const [gameEnded, setGameEnded] = useState(false);
  const [consecutivePasses, setConsecutivePasses] = useState(0);
  const [moveHistory, setMoveHistory] = useState([]);
  const [draggedPiece, setDraggedPiece] = useState(null);
  const [dragHoverPosition, setDragHoverPosition] = useState(null);
  const [touchedPiece, setTouchedPiece] = useState(null);
  const [touchPosition, setTouchPosition] = useState(null);
  const [botDifficulty, setBotDifficulty] = useState('easy');
  const [botName] = useState(() => {
    const botNames = ['David', 'Sarah', 'Alex', 'Emma', 'Chris', 'Maya', 'Jordan', 'Riley', 'Morgan', 'Taylor'];
    return `Bot ${botNames[Math.floor(Math.random() * botNames.length)]}`;
  });
  const [gameStartScreen, setGameStartScreen] = useState(true);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [mobileTab, setMobileTab] = useState('your');
  const [timerDuration, setTimerDuration] = useState(60);
  const [placedCells, setPlacedCells] = useState(new Set());
  const [playerColor, setPlayerColor] = useState(initialColor);
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const timerRef = useRef(null);
  const tickSoundRef = useRef(null);

  // Color mapping: blue pairs with red, orange pairs with purple
  const getOpponentColor = (color) => {
    if (color === 'blue') return 'red';
    if (color === 'red') return 'blue';
    if (color === 'orange') return 'purple';
    if (color === 'purple') return 'orange';
    return 'red';
  };

  const getColorStyles = (color) => {
    const colors = {
      blue: { bg: '#4a90e2', border: '#357abd' },
      red: { bg: '#e74c3c', border: '#c0392b' },
      orange: { bg: '#ff8c42', border: '#e67e22' },
      purple: { bg: '#7d3c98', border: '#6c3483' }
    };
    return colors[color] || colors.blue;
  };

  // Get current piece shape with transformations
  const getCurrentPieceShape = useCallback(() => {
    if (!selectedPiece) return null;
    let shape = selectedPiece.shape;

    // Apply rotations
    for (let i = 0; i < pieceTransform.rotation; i++) {
      shape = rotatePiece(shape);
    }

    // Apply flip
    if (pieceTransform.flipped) {
      shape = flipPiece(shape);
    }

    return shape;
  }, [selectedPiece, pieceTransform]);

  // Check if position is valid on board
  const isValidPosition = (row, col, shape) => {
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) {
          const boardRow = row + r;
          const boardCol = col + c;
          if (boardRow < 0 || boardRow >= 14 || boardCol < 0 || boardCol >= 14) {
            return false;
          }
          if (board[boardRow][boardCol] !== null) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Check if move is legal (follows Block Rivals rules)
  const isLegalMove = useCallback((row, col, shape, player) => {
    if (!isValidPosition(row, col, shape)) return { legal: false };

    const isFirstMove = moveHistory.filter(m => m.player === player).length === 0;

    // First move must touch corner
    if (isFirstMove) {
      const corner1 = player === 1 ? [4, 4] : [9, 9];
      let touchesCorner = false;

      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            const boardRow = row + r;
            const boardCol = col + c;
            if (boardRow === corner1[0] && boardCol === corner1[1]) {
              touchesCorner = true;
            }
          }
        }
      }

      if (!touchesCorner) return { legal: false };
      return { legal: true, connectingCorner: corner1 };
    }

    // Check for edge adjacency (not allowed)
    let hasEdgeAdjacency = false;
    let hasCornerConnection = false;
    let connectingCorner = null;

    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) {
          const boardRow = row + r;
          const boardCol = col + c;

          // Check edges
          const edges = [
            [boardRow - 1, boardCol],
            [boardRow + 1, boardCol],
            [boardRow, boardCol - 1],
            [boardRow, boardCol + 1]
          ];

          for (const [er, ec] of edges) {
            if (er >= 0 && er < 14 && ec >= 0 && ec < 14) {
              if (board[er][ec] === player) {
                hasEdgeAdjacency = true;
              }
            }
          }

          // Check corners
          const corners = [
            [boardRow - 1, boardCol - 1],
            [boardRow - 1, boardCol + 1],
            [boardRow + 1, boardCol - 1],
            [boardRow + 1, boardCol + 1]
          ];

          for (const [cr, cc] of corners) {
            if (cr >= 0 && cr < 14 && cc >= 0 && cc < 14) {
              if (board[cr][cc] === player) {
                hasCornerConnection = true;
                connectingCorner = [cr, cc];
              }
            }
          }
        }
      }
    }

    if (hasEdgeAdjacency) return { legal: false };
    if (!hasCornerConnection) return { legal: false };

    return { legal: true, connectingCorner };
  }, [board, moveHistory]);

  // Find all legal positions where the piece can be placed
  const findLegalPositions = useCallback(() => {
    if (!selectedPiece || !gameStarted || gameEnded) return [];

    const shape = getCurrentPieceShape();
    if (!shape) return [];

    const legalTiles = new Map(); // Map to store all legal tiles with their placement info

    // Check every possible position on the board
    for (let row = 0; row < 14; row++) {
      for (let col = 0; col < 14; col++) {
        const result = isLegalMove(row, col, shape, currentPlayer);

        if (result.legal) {
          // For each valid placement position, mark all tiles the piece would occupy
          for (let r = 0; r < shape.length; r++) {
            for (let c = 0; c < shape[0].length; c++) {
              if (shape[r][c] === 1) {
                const tileRow = row + r;
                const tileCol = col + c;
                const key = `${tileRow},${tileCol}`;

                // Store this tile as a legal position
                if (!legalTiles.has(key)) {
                  legalTiles.set(key, {
                    row: tileRow,
                    col: tileCol,
                    placementRow: row,
                    placementCol: col
                  });
                }
              }
            }
          }
        }
      }
    }

    return Array.from(legalTiles.values());
  }, [selectedPiece, getCurrentPieceShape, isLegalMove, currentPlayer, gameStarted, gameEnded]);

  // Update legal positions when piece or transform changes
  useEffect(() => {
    if (selectedPiece && currentPlayer === 1) {
      setLegalPositions(findLegalPositions());
    } else {
      setLegalPositions([]);
    }
  }, [selectedPiece, pieceTransform, findLegalPositions, currentPlayer]);

  // Timer logic
  useEffect(() => {
    if (!gameStarted || gameEnded) return;

    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          return -1; // Signal that time is up
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [gameStarted, gameEnded, timerDuration]);

  // Handle when timer reaches 0
  useEffect(() => {
    if (timer === -1 && !gameEnded && gameStarted) {
      // Auto-confirm or pass
      if (pendingMove) {
        handleConfirm();
      } else {
        handlePass();
      }
    }
  }, [timer, gameEnded, gameStarted, pendingMove]);

  // Play ticking sound when timer is low (only for games >= 1 minute)
  useEffect(() => {
    if (!gameStarted || gameEnded || timerDuration < 60) return;

    if (timer <= 5 && timer > 0) {
      // Play tick sound
      if (!tickSoundRef.current) {
        tickSoundRef.current = new Audio();
        tickSoundRef.current.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
      }

      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 1200; // Frequency in Hz (higher pitched)
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    }
  }, [timer, gameStarted, gameEnded, timerDuration]);

  // Reset timer when turn changes
  useEffect(() => {
    setTimer(timerDuration);
  }, [currentPlayer, timerDuration]);

  // Bot move logic
  useEffect(() => {
    if (currentPlayer === 2 && !gameEnded && gameStarted) {
      const timeout = setTimeout(() => {
        makeBotMove();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [currentPlayer, gameEnded, gameStarted]);

  const makeBotMove = () => {
    const startTime = Date.now();
    const availablePieces = player2Pieces.filter(p => !p.used);
    let moveToMake = null;

    if (botDifficulty === 'easy') {
      // Easy mode: Play smallest piece (randomize if multiple pieces have same size)
      let smallestMoves = [];
      let minPoints = Infinity;

      for (const piece of availablePieces) {
        // Count tiles in this piece
        let pieceSize = 0;
        for (let r = 0; r < piece.shape.length; r++) {
          for (let c = 0; c < piece.shape[0].length; c++) {
            if (piece.shape[r][c] === 1) pieceSize++;
          }
        }

        for (let rotation = 0; rotation < 4; rotation++) {
          for (let flipped = 0; flipped < 2; flipped++) {
            let shape = piece.shape;

            // Apply transformations
            for (let i = 0; i < rotation; i++) {
              shape = rotatePiece(shape);
            }
            if (flipped === 1) {
              shape = flipPiece(shape);
            }

            // Try all positions
            for (let row = 0; row < 14; row++) {
              for (let col = 0; col < 14; col++) {
                const result = isLegalMove(row, col, shape, 2);
                if (result.legal) {
                  if (pieceSize < minPoints) {
                    // Found smaller piece, reset array
                    minPoints = pieceSize;
                    smallestMoves = [{ row, col, shape, pieceId: piece.id }];
                  } else if (pieceSize === minPoints) {
                    // Same size, add to candidates
                    smallestMoves.push({ row, col, shape, pieceId: piece.id });
                  }
                  // Only need one legal position per piece variant
                  break;
                }
              }
            }
          }
        }
      }

      // Randomly select from smallest moves
      if (smallestMoves.length > 0) {
        moveToMake = smallestMoves[Math.floor(Math.random() * smallestMoves.length)];
      }
    } else if (botDifficulty === 'medium') {
      // Medium mode: Choose any random legal move
      let allLegalMoves = [];

      for (const piece of availablePieces) {
        for (let rotation = 0; rotation < 4; rotation++) {
          for (let flipped = 0; flipped < 2; flipped++) {
            let shape = piece.shape;

            // Apply transformations
            for (let i = 0; i < rotation; i++) {
              shape = rotatePiece(shape);
            }
            if (flipped === 1) {
              shape = flipPiece(shape);
            }

            // Try all positions
            for (let row = 0; row < 14; row++) {
              for (let col = 0; col < 14; col++) {
                const result = isLegalMove(row, col, shape, 2);
                if (result.legal) {
                  allLegalMoves.push({ row, col, shape, pieceId: piece.id });
                }
              }
            }
          }
        }
      }

      // Randomly select from all legal moves
      if (allLegalMoves.length > 0) {
        moveToMake = allLegalMoves[Math.floor(Math.random() * allLegalMoves.length)];
      }
    } else {
      // Hard mode: Play largest piece (randomize if multiple pieces have same size)
      let largestMoves = [];
      let maxPoints = 0;

      for (const piece of availablePieces) {
        // Count tiles in this piece
        let pieceSize = 0;
        for (let r = 0; r < piece.shape.length; r++) {
          for (let c = 0; c < piece.shape[0].length; c++) {
            if (piece.shape[r][c] === 1) pieceSize++;
          }
        }

        for (let rotation = 0; rotation < 4; rotation++) {
          for (let flipped = 0; flipped < 2; flipped++) {
            let shape = piece.shape;

            // Apply transformations
            for (let i = 0; i < rotation; i++) {
              shape = rotatePiece(shape);
            }
            if (flipped === 1) {
              shape = flipPiece(shape);
            }

            // Try all positions
            for (let row = 0; row < 14; row++) {
              for (let col = 0; col < 14; col++) {
                const result = isLegalMove(row, col, shape, 2);
                if (result.legal) {
                  if (pieceSize > maxPoints) {
                    // Found larger piece, reset array
                    maxPoints = pieceSize;
                    largestMoves = [{ row, col, shape, pieceId: piece.id }];
                  } else if (pieceSize === maxPoints) {
                    // Same size, add to candidates
                    largestMoves.push({ row, col, shape, pieceId: piece.id });
                  }
                  // Only need one legal position per piece variant
                  break;
                }
              }
            }
          }
        }
      }

      // Randomly select from largest moves
      if (largestMoves.length > 0) {
        moveToMake = largestMoves[Math.floor(Math.random() * largestMoves.length)];
      }
    }

    // Calculate remaining time to reach minimum 0.5 seconds
    const elapsed = Date.now() - startTime;
    const delay = Math.max(500 - elapsed, 0);

    // Execute the move after ensuring minimum 1 second delay
    setTimeout(() => {
      if (moveToMake) {
        placePiece(moveToMake.row, moveToMake.col, moveToMake.shape, moveToMake.pieceId, 2);
      } else {
        // No legal moves, pass
        handlePass();
      }
    }, delay);
  };

  const placePiece = (row, col, shape, pieceId, player) => {
    const newBoard = board.map(r => [...r]);
    let tilesPlaced = 0;
    const newPlacedCells = new Set();

    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[0].length; c++) {
        if (shape[r][c] === 1) {
          newBoard[row + r][col + c] = player;
          newPlacedCells.add(`${row + r},${col + c}`);
          tilesPlaced++;
        }
      }
    }

    setBoard(newBoard);
    setPlacedCells(newPlacedCells);

    // Clear the animation after it completes
    setTimeout(() => {
      setPlacedCells(new Set());
    }, 800);

    // Update pieces
    if (player === 1) {
      setPlayer1Pieces(prev => prev.map(p =>
        p.id === pieceId ? { ...p, used: true } : p
      ));
      setScore(prev => ({ ...prev, player1: prev.player1 + tilesPlaced }));
    } else {
      setPlayer2Pieces(prev => prev.map(p =>
        p.id === pieceId ? { ...p, used: true } : p
      ));
      setScore(prev => ({ ...prev, player2: prev.player2 + tilesPlaced }));
    }

    // Add to history
    setMoveHistory(prev => [...prev, { player, board: newBoard, pieceId, tilesPlaced }]);

    // Play sound
    playPlaceSound();

    // Reset state
    setSelectedPiece(null);
    setPieceTransform({ rotation: 0, flipped: false });
    setPendingMove(null);
    setLegalPositions([]);
    setConsecutivePasses(0);

    // Switch player and reset timer
    setCurrentPlayer(player === 1 ? 2 : 1);
    setTimer(timerDuration);
  };

  const handlePieceSelect = (piece) => {
    if (currentPlayer !== 1 || piece.used || gameEnded) return;

    playSelectSound();
    setSelectedPiece(piece);
    setPieceTransform({ rotation: 0, flipped: false });
    setPendingMove(null);
  };

  // Find valid positions for a shape where one of its cells occupies the target board cell
  const findPositionsWithCellAt = (shape, targetRow, targetCol) => {
    const validPositions = [];

    // For each cell in the shape, calculate where the piece would need to be positioned
    // so that this cell lands on the target board position
    for (let shapeRow = 0; shapeRow < shape.length; shapeRow++) {
      for (let shapeCol = 0; shapeCol < shape[0].length; shapeCol++) {
        if (shape[shapeRow][shapeCol] === 1) {
          // Calculate the top-left position that would place this shape cell at the target
          const pieceRow = targetRow - shapeRow;
          const pieceCol = targetCol - shapeCol;

          // Check if this placement is legal
          const result = isLegalMove(pieceRow, pieceCol, shape, currentPlayer);
          if (result.legal) {
            // Also verify that this position actually connects to the target corner
            if (result.connectingCorner &&
                result.connectingCorner[0] === targetRow &&
                result.connectingCorner[1] === targetCol) {
              validPositions.push({ row: pieceRow, col: pieceCol });
            }
          }
        }
      }
    }

    return validPositions;
  };

  // Check if rotating the pending move would still be legal
  const canRotatePendingMove = () => {
    if (!pendingMove || !selectedPiece) return false;

    const isFirstMove = moveHistory.filter(m => m.player === currentPlayer).length === 0;
    const currentShape = pendingMove.shape;

    // Try all rotations to see if any are legal AND different from current shape
    for (let attempt = 1; attempt <= 4; attempt++) {
      const nextRotation = (pieceTransform.rotation + attempt) % 4;
      let shape = selectedPiece.shape;

      // Apply rotation
      for (let i = 0; i < nextRotation; i++) {
        shape = rotatePiece(shape);
      }

      // Apply flip if needed
      if (pieceTransform.flipped) {
        shape = flipPiece(shape);
      }

      // Check if this rotated shape is different from the current shape
      let shapesAreSame = true;
      if (shape.length !== currentShape.length || shape[0].length !== currentShape[0].length) {
        shapesAreSame = false;
      } else {
        for (let r = 0; r < shape.length; r++) {
          for (let c = 0; c < shape[0].length; c++) {
            if (shape[r][c] !== currentShape[r][c]) {
              shapesAreSame = false;
              break;
            }
          }
          if (!shapesAreSame) break;
        }
      }

      // Skip if this rotation results in the same shape
      if (shapesAreSame) continue;

      // For first move, check if we can find a position where the piece touches the starting corner
      if (isFirstMove && pendingMove.anchorCell) {
        const [anchorRow, anchorCol] = pendingMove.anchorCell;

        // Try to find a valid position where a cell of the rotated shape touches the anchor
        for (let r = 0; r < shape.length; r++) {
          for (let c = 0; c < shape[0].length; c++) {
            if (shape[r][c] === 1) {
              const testRow = anchorRow - r;
              const testCol = anchorCol - c;
              const result = isLegalMove(testRow, testCol, shape, currentPlayer);
              if (result.legal) {
                return true;
              }
            }
          }
        }
      } else {
        // For non-first moves, check if this rotated shape is legal at the current position
        const result = isLegalMove(pendingMove.row, pendingMove.col, shape, currentPlayer);
        if (result.legal) {
          return true;
        }
      }
    }

    return false;
  };

  // Check if flipping the pending move would still be legal
  const canFlipPendingMove = () => {
    if (!pendingMove || !selectedPiece) return false;

    const isFirstMove = moveHistory.filter(m => m.player === currentPlayer).length === 0;

    const newTransform = {
      ...pieceTransform,
      flipped: !pieceTransform.flipped
    };

    let shape = selectedPiece.shape;

    // Apply rotation
    for (let i = 0; i < newTransform.rotation; i++) {
      shape = rotatePiece(shape);
    }

    // Apply new flip
    if (newTransform.flipped) {
      shape = flipPiece(shape);
    }

    // Check if the flipped shape is different from the current shape
    const currentShape = pendingMove.shape;
    let shapesAreSame = true;
    if (shape.length !== currentShape.length || shape[0].length !== currentShape[0].length) {
      shapesAreSame = false;
    } else {
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] !== currentShape[r][c]) {
            shapesAreSame = false;
            break;
          }
        }
        if (!shapesAreSame) break;
      }
    }

    // If shapes are the same, flipping would make no difference
    if (shapesAreSame) return false;

    // For first move, check if we can find a position where the piece touches the starting corner
    if (isFirstMove && pendingMove.anchorCell) {
      const [anchorRow, anchorCol] = pendingMove.anchorCell;

      // Try to find a valid position where a cell of the flipped shape touches the anchor
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            const testRow = anchorRow - r;
            const testCol = anchorCol - c;
            const result = isLegalMove(testRow, testCol, shape, currentPlayer);
            if (result.legal) {
              return true;
            }
          }
        }
      }
      return false;
    }

    // Check if this flipped shape is legal at the current position
    const result = isLegalMove(pendingMove.row, pendingMove.col, shape, currentPlayer);
    return result.legal;
  };

  const handleRotate = () => {
    if (!selectedPiece) return;
    playRotateSound();

    // If there's a pending move, find the next legal rotation
    if (pendingMove) {
      const isFirstMove = moveHistory.filter(m => m.player === currentPlayer).length === 0;

      // Try up to 4 rotations to find the next legal one
      for (let attempt = 1; attempt <= 4; attempt++) {
        const nextRotation = (pieceTransform.rotation + attempt) % 4;
        let shape = selectedPiece.shape;

        // Apply rotation
        for (let i = 0; i < nextRotation; i++) {
          shape = rotatePiece(shape);
        }

        // Apply flip if needed
        if (pieceTransform.flipped) {
          shape = flipPiece(shape);
        }

        // For first move, find a position where the piece touches the starting corner
        if (isFirstMove && pendingMove.anchorCell) {
          const [anchorRow, anchorCol] = pendingMove.anchorCell;

          // Try to find a valid position where a cell of the rotated shape touches the anchor
          for (let r = 0; r < shape.length; r++) {
            for (let c = 0; c < shape[0].length; c++) {
              if (shape[r][c] === 1) {
                const testRow = anchorRow - r;
                const testCol = anchorCol - c;
                const result = isLegalMove(testRow, testCol, shape, currentPlayer);
                if (result.legal) {
                  // Found a legal rotation - update with new position
                  setPieceTransform({ rotation: nextRotation, flipped: pieceTransform.flipped });
                  setPendingMove({
                    row: testRow,
                    col: testCol,
                    shape,
                    pieceId: pendingMove.pieceId,
                    anchorCell: result.connectingCorner
                  });
                  return;
                }
              }
            }
          }
        } else {
          // For non-first moves, check if this rotated shape is legal at the current position
          const result = isLegalMove(pendingMove.row, pendingMove.col, shape, currentPlayer);
          if (result.legal) {
            // Found a legal rotation - update
            setPieceTransform({ rotation: nextRotation, flipped: pieceTransform.flipped });
            setPendingMove({
              row: pendingMove.row,
              col: pendingMove.col,
              shape,
              pieceId: pendingMove.pieceId,
              anchorCell: result.connectingCorner
            });
            return;
          }
        }
      }
      // If no legal rotation found, keep the current state (shouldn't happen if button is properly disabled)
    } else {
      // No pending move, just rotate normally
      const newTransform = {
        rotation: (pieceTransform.rotation + 1) % 4,
        flipped: pieceTransform.flipped
      };
      setPieceTransform(newTransform);
    }
  };

  const handleFlip = () => {
    if (!selectedPiece) return;
    playFlipSound();

    if (pendingMove) {
      const isFirstMove = moveHistory.filter(m => m.player === currentPlayer).length === 0;

      // Try flipping - check if the flipped version is legal at current position
      const newFlipped = !pieceTransform.flipped;
      let shape = selectedPiece.shape;

      // Apply rotation
      for (let i = 0; i < pieceTransform.rotation; i++) {
        shape = rotatePiece(shape);
      }

      // Apply new flip
      if (newFlipped) {
        shape = flipPiece(shape);
      }

      // For first move, find a position where the piece touches the starting corner
      if (isFirstMove && pendingMove.anchorCell) {
        const [anchorRow, anchorCol] = pendingMove.anchorCell;

        // Try to find a valid position where a cell of the flipped shape touches the anchor
        for (let r = 0; r < shape.length; r++) {
          for (let c = 0; c < shape[0].length; c++) {
            if (shape[r][c] === 1) {
              const testRow = anchorRow - r;
              const testCol = anchorCol - c;
              const result = isLegalMove(testRow, testCol, shape, currentPlayer);
              if (result.legal) {
                // Found a legal flip - update with new position
                setPieceTransform({ rotation: pieceTransform.rotation, flipped: newFlipped });
                setPendingMove({
                  row: testRow,
                  col: testCol,
                  shape,
                  pieceId: pendingMove.pieceId,
                  anchorCell: result.connectingCorner
                });
                return;
              }
            }
          }
        }
      } else {
        // Check if this flipped shape is legal at the current position
        const result = isLegalMove(pendingMove.row, pendingMove.col, shape, currentPlayer);
        if (result.legal) {
          // Found a legal flip - update
          setPieceTransform({ rotation: pieceTransform.rotation, flipped: newFlipped });
          setPendingMove({
            row: pendingMove.row,
            col: pendingMove.col,
            shape,
            pieceId: pendingMove.pieceId,
            anchorCell: result.connectingCorner
          });
        }
      }
      // If not legal, keep the current state (don't remove piece)
    } else {
      // No pending move, just flip normally
      const newTransform = {
        rotation: pieceTransform.rotation,
        flipped: !pieceTransform.flipped
      };
      setPieceTransform(newTransform);
    }
  };

  const handleBoardClick = (row, col, event) => {
    if (!selectedPiece || currentPlayer !== 1 || pendingMove || gameEnded) return;

    // First try exact cell match
    let legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);

    // If no exact match and on mobile with touch event, try proximity detection
    if (!legalPos && viewMode === 'mobile' && event && event.type === 'click' && event.clientX && event.clientY) {
      legalPos = findNearestLegalPosition(event.clientX, event.clientY);
    }

    if (!legalPos) return;

    // Get the placement position for this tile
    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape, currentPlayer);

    if (result.legal) {
      // Use the connecting corner as the anchor (for first moves, this is the starting corner)
      setPendingMove({
        row: legalPos.placementRow,
        col: legalPos.placementCol,
        shape,
        pieceId: selectedPiece.id,
        anchorCell: result.connectingCorner // The corner that the piece connects to
      });
    }
  };

  // Helper function to find nearest legal position within threshold
  const findNearestLegalPosition = (clientX, clientY, threshold) => {
    // Use responsive threshold based on view mode if not provided
    if (threshold === undefined) {
      threshold = viewMode === 'mobile' ? 30 : 50;
    }

    // Get all board cells
    const boardCells = document.querySelectorAll('.board .cell');
    let nearestPosition = null;
    let minDistance = Infinity; // Start with infinity to find absolute closest

    boardCells.forEach(cell => {
      const row = parseInt(cell.dataset.row);
      const col = parseInt(cell.dataset.col);

      // Check if this is a legal position
      const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
      if (!legalPos) return;

      // Calculate distance from mouse/touch position to cell center
      const rect = cell.getBoundingClientRect();
      const cellCenterX = rect.left + rect.width / 2;
      const cellCenterY = rect.top + rect.height / 2;
      const distance = Math.sqrt(
        Math.pow(clientX - cellCenterX, 2) +
        Math.pow(clientY - cellCenterY, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestPosition = legalPos;
      }
    });

    // Only return if within threshold
    if (minDistance <= threshold) {
      return nearestPosition;
    }
    return null;
  };

  // Drag and drop handlers
  const handleDragStart = (e, piece) => {
    if (currentPlayer !== 1 || piece.used || gameEnded) {
      e.preventDefault();
      return;
    }
    setDraggedPiece(piece);
    setTouchedPiece(piece); // Track for visual hiding
    setTouchPosition({ x: e.clientX, y: e.clientY }); // Set initial position

    // Only select the piece if it's not already selected
    // This preserves rotation/flip if you've already selected and transformed it
    if (selectedPiece?.id !== piece.id) {
      handlePieceSelect(piece);
    }

    // Hide the default drag ghost
    const ghost = document.createElement('div');
    ghost.style.opacity = '0';
    e.dataTransfer.setDragImage(ghost, 0, 0);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrag = (e) => {
    // Update position during drag
    if (e.clientX !== 0 && e.clientY !== 0) {
      setTouchPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleDragOver = (e, row, col) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Update position during drag over board
    if (e.clientX !== 0 && e.clientY !== 0) {
      setTouchPosition({ x: e.clientX, y: e.clientY });
    }

    // Show preview if dragging over a legal position
    if (!draggedPiece || !selectedPiece || currentPlayer !== 1 || pendingMove || gameEnded) {
      setDragHoverPosition(null);
      return;
    }

    // First try exact cell match
    let legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);

    // If no exact match, find nearest legal position within threshold
    if (!legalPos && e.clientX !== 0 && e.clientY !== 0) {
      legalPos = findNearestLegalPosition(e.clientX, e.clientY);
    }

    if (!legalPos) {
      setDragHoverPosition(null);
      return;
    }

    // Get the placement position for this tile
    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape, currentPlayer);

    if (result.legal) {
      setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleDrop = (e, row, col) => {
    e.preventDefault();
    if (!draggedPiece || !selectedPiece || currentPlayer !== 1 || pendingMove || gameEnded) return;

    handleBoardClick(row, col);
    setDraggedPiece(null);
    setDragHoverPosition(null);
    setTouchedPiece(null);
    setTouchPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedPiece(null);
    setDragHoverPosition(null);
    setTouchedPiece(null);
    setTouchPosition(null);
  };

  // Touch handlers for mobile drag and drop
  const handleTouchStart = (e, piece) => {
    if (currentPlayer !== 1 || piece.used || gameEnded) {
      return;
    }

    e.preventDefault();
    setTouchedPiece(piece);

    // Select the piece if not already selected
    if (selectedPiece?.id !== piece.id) {
      handlePieceSelect(piece);
    }

    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    // Always prevent default to stop scrolling when touching a piece
    if (touchedPiece) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!touchedPiece || !selectedPiece || currentPlayer !== 1 || pendingMove || gameEnded) {
      return;
    }

    const touch = e.touches[0];
    setTouchPosition({ x: touch.clientX, y: touch.clientY });

    // First try to find exact cell at touch position
    let legalPos = null;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row !== undefined && element.dataset.col !== undefined) {
      const row = parseInt(element.dataset.row);
      const col = parseInt(element.dataset.col);
      legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    }

    // If no exact match, find nearest legal position within threshold
    if (!legalPos) {
      legalPos = findNearestLegalPosition(touch.clientX, touch.clientY);
    }

    if (legalPos) {
      const shape = getCurrentPieceShape();
      const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape, currentPlayer);

      if (result.legal) {
        setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
      } else {
        setDragHoverPosition(null);
      }
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchedPiece || !selectedPiece || currentPlayer !== 1 || gameEnded) {
      setTouchedPiece(null);
      setTouchPosition(null);
      setDragHoverPosition(null);
      return;
    }

    e.preventDefault();
    const touch = e.changedTouches[0];

    // First try to find exact cell at touch position
    let targetRow = null;
    let targetCol = null;
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row !== undefined && element.dataset.col !== undefined) {
      targetRow = parseInt(element.dataset.row);
      targetCol = parseInt(element.dataset.col);
    } else {
      // If no exact cell found, try to find nearest legal position
      const nearestPos = findNearestLegalPosition(touch.clientX, touch.clientY);
      if (nearestPos) {
        targetRow = nearestPos.row;
        targetCol = nearestPos.col;
      }
    }

    // Place piece if we found a valid position
    if (targetRow !== null && targetCol !== null) {
      handleBoardClick(targetRow, targetCol);
    }

    setTouchedPiece(null);
    setTouchPosition(null);
    setDragHoverPosition(null);
  };

  const handleMouseEnter = (row, col) => {
    // Only show hover preview when piece is selected but not yet placed, and not during drag
    if (!selectedPiece || currentPlayer !== 1 || pendingMove || draggedPiece || gameEnded) {
      return;
    }

    // Check if this is a legal tile
    const legalPos = legalPositions.find(pos => pos.row === row && pos.col === col);
    if (!legalPos) {
      setDragHoverPosition(null);
      return;
    }

    // Get the placement position for this tile
    const shape = getCurrentPieceShape();
    const result = isLegalMove(legalPos.placementRow, legalPos.placementCol, shape, currentPlayer);

    if (result.legal) {
      setDragHoverPosition({ row: legalPos.placementRow, col: legalPos.placementCol, shape });
    } else {
      setDragHoverPosition(null);
    }
  };

  const handleMouseLeave = () => {
    // Only clear hover preview if not dragging
    if (!draggedPiece) {
      setDragHoverPosition(null);
    }
  };

  const handleConfirm = () => {
    if (!pendingMove) return;
    placePiece(pendingMove.row, pendingMove.col, pendingMove.shape, pendingMove.pieceId, currentPlayer);
  };

  const handleUndo = () => {
    if (pendingMove) {
      playUndoSound();
    }
    setPendingMove(null);
  };

  const handlePass = () => {
    playPassSound();
    const newPasses = consecutivePasses + 1;
    setConsecutivePasses(newPasses);

    if (newPasses >= 2) {
      setGameEnded(true);
      return;
    }

    setSelectedPiece(null);
    setPendingMove(null);
    setLegalPositions([]);
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    setTimer(timerDuration);
  };

  const handleStartGame = () => {
    setGameStartScreen(false);
    setBoard(initializeBoard());
    setPlayer1Pieces(initializePieces());
    setPlayer2Pieces(initializePieces());
    // Randomly choose starting player (1 or 2)
    setCurrentPlayer(Math.random() < 0.5 ? 1 : 2);
    setSelectedPiece(null);
    setPieceTransform({ rotation: 0, flipped: false });
    setPendingMove(null);
    setLegalPositions([]);
    setScore({ player1: 0, player2: 0 });
    setTimer(timerDuration);
    setGameStarted(true);
    setGameEnded(false);
    setConsecutivePasses(0);
    setMoveHistory([]);
    setDraggedPiece(null);
  };

  const handleNewGame = () => {
    setGameStartScreen(true);
  };

  const handleReturnToMenu = () => {
    setShowQuitConfirm(true);
  };

  const confirmQuit = () => {
    setShowQuitConfirm(false);
    if (onReturnToMenu) {
      // Return to main menu
      onReturnToMenu();
    } else {
      // Reset to start screen (legacy behavior)
      setGameStartScreen(true);
      setBoard(initializeBoard());
      setPlayer1Pieces(initializePieces());
      setPlayer2Pieces(initializePieces());
      setCurrentPlayer(1);
      setSelectedPiece(null);
      setPieceTransform({ rotation: 0, flipped: false });
      setPendingMove(null);
      setLegalPositions([]);
      setScore({ player1: 0, player2: 0 });
      setTimer(timerDuration);
      setGameStarted(true);
      setGameEnded(false);
      setConsecutivePasses(0);
    }
    setMoveHistory([]);
    setDraggedPiece(null);
  };

  const cancelQuit = () => {
    setShowQuitConfirm(false);
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
  };

  // Check if player has legal moves
  const hasLegalMoves = useCallback((player) => {
    const pieces = player === 1 ? player1Pieces : player2Pieces;
    const availablePieces = pieces.filter(p => !p.used);

    for (const piece of availablePieces) {
      for (let rotation = 0; rotation < 4; rotation++) {
        for (let flipped = 0; flipped < 2; flipped++) {
          let shape = piece.shape;

          for (let i = 0; i < rotation; i++) {
            shape = rotatePiece(shape);
          }
          if (flipped === 1) {
            shape = flipPiece(shape);
          }

          for (let row = 0; row < 14; row++) {
            for (let col = 0; col < 14; col++) {
              if (isLegalMove(row, col, shape, player).legal) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }, [player1Pieces, player2Pieces, isLegalMove]);

  const player1HasMoves = hasLegalMoves(1);
  const player2HasMoves = hasLegalMoves(2);

  // Render preview of pending move or drag hover
  const renderBoard = () => {
    const boardDisplay = board.map(r => [...r]);

    if (pendingMove) {
      const { row, col, shape } = pendingMove;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            boardDisplay[row + r][col + c] = 'preview';
          }
        }
      }
    } else if (dragHoverPosition) {
      // Show preview during drag hover
      const { row, col, shape } = dragHoverPosition;
      for (let r = 0; r < shape.length; r++) {
        for (let c = 0; c < shape[0].length; c++) {
          if (shape[r][c] === 1) {
            boardDisplay[row + r][col + c] = 'preview';
          }
        }
      }
    }

    return boardDisplay;
  };

  const boardDisplay = renderBoard();

  if (gameStartScreen) {
    return (
      <div className={viewMode === 'mobile' ? 'App mobile-view' : 'App'}>
        <h1>Block Rivals</h1>
        <div className="menu-container">
          <div className="start-screen">
            <h2>Select Bot Difficulty</h2>
            <div className="difficulty-buttons">
              <button
                className={`difficulty-btn ${botDifficulty === 'easy' ? 'selected' : ''}`}
                onClick={() => { playClickSound(); setBotDifficulty('easy'); }}
              >
                Easy
              </button>
              <button
                className={`difficulty-btn ${botDifficulty === 'medium' ? 'selected' : ''}`}
                onClick={() => { playClickSound(); setBotDifficulty('medium'); }}
              >
                Medium
              </button>
              <button
                className={`difficulty-btn ${botDifficulty === 'hard' ? 'selected' : ''}`}
                onClick={() => { playClickSound(); setBotDifficulty('hard'); }}
              >
                Hard
              </button>
            </div>

            <div className="timer-selector">
              <h2>Timer per Turn</h2>
              <div className="timer-slider-container">
                <input
                  type="range"
                  min="15"
                  max="300"
                  step="15"
                  value={timerDuration}
                  onChange={(e) => setTimerDuration(Number(e.target.value))}
                  className="timer-slider"
                />
                <div className="timer-value">{formatTime(timerDuration)}</div>
              </div>
            </div>

            <button className="start-game-btn" onClick={() => { playTabSound(); handleStartGame(); }}>
              Start Game
            </button>
            <button className="menu-btn secondary" onClick={() => { playClickSound(); onReturnToMenu(); }}>
              Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Mobile view layout
  if (viewMode === 'mobile') {
    return (
      <div className="App mobile-view">
        <div className="title-container">
          <h1>Block Rivals</h1>
          {!gameStartScreen && (
            <button
              onClick={handleReturnToMenu}
              className="menu-btn-title"
              title="Quit to Menu"
            >
              ✕
            </button>
          )}
        </div>


        <div className="mobile-game-container">
          <div className="mobile-player-info-container">
            <div
              className={`player-info mobile-player-card ${currentPlayer === 1 ? 'active' : ''} ${mobileTab === 'your' ? 'selected-tab' : ''}`}
              onClick={() => setMobileTab('your')}
              style={{ cursor: 'pointer' }}
            >
              <div className="mobile-info-row">
                <span className="mobile-player-name">{playerName}</span>
                <span className="mobile-score-text">{score.player1}</span>
                {currentPlayer === 1 && !gameEnded && (
                  <span className="mobile-timer-text">{Math.max(0, timer)}s</span>
                )}
              </div>
            </div>

            <div
              className={`player-info mobile-player-card ${currentPlayer === 2 ? 'active' : ''} ${mobileTab === 'opponent' ? 'selected-tab' : ''}`}
              onClick={() => setMobileTab('opponent')}
              style={{ cursor: 'pointer' }}
            >
              <div className="mobile-info-row">
                <span className="mobile-player-name">{botName}</span>
                <span className="mobile-score-text">{score.player2}</span>
                {currentPlayer === 2 && !gameEnded && (
                  <span className="mobile-timer-text">{Math.max(0, timer)}s</span>
                )}
              </div>
            </div>
          </div>

          <div className="game-over-placeholder">
            {gameEnded && (
              <div className="game-over">
                <h2>Game Over!</h2>
                <p>{score.player1 > score.player2 ? `${playerName} Wins!` : score.player2 > score.player1 ? `${botName} Wins!` : "It's a Tie!"}</p>
                <button onClick={handleNewGame} className="new-game-btn">New Game</button>
              </div>
            )}
          </div>

          <div className="board" style={{
            '--player1-color': getColorStyles(playerColor).bg,
            '--player1-border': getColorStyles(playerColor).border,
            '--player2-color': getColorStyles(getOpponentColor(playerColor)).bg,
            '--player2-border': getColorStyles(getOpponentColor(playerColor)).border
          }}>
            {boardDisplay.map((row, r) => (
              <div key={r} className="board-row">
                {row.map((cell, c) => {
                  const isLegal = !pendingMove && legalPositions.some(pos => pos.row === r && pos.col === c);
                  const isPlayer1Start = r === 4 && c === 4;
                  const isPlayer2Start = r === 9 && c === 9;
                  const isPlaced = placedCells.has(`${r},${c}`);

                  const player1Styles = cell === 1 ? getColorStyles(playerColor) : {};
                  const player2Styles = cell === 2 ? getColorStyles(getOpponentColor(playerColor)) : {};
                  const cellStyle = cell === 1 ? {
                    backgroundColor: player1Styles.bg,
                    borderColor: player1Styles.border
                  } : cell === 2 ? {
                    backgroundColor: player2Styles.bg,
                    borderColor: player2Styles.border
                  } : {};

                  return (
                    <div
                      key={c}
                      className={`cell ${cell === 1 ? 'player1' : cell === 2 ? 'player2' : ''}
                                 ${cell === 'preview' ? 'preview' : ''}
                                 ${isLegal ? 'legal' : ''}
                                 ${isPlayer1Start ? 'start-corner-p1' : ''}
                                 ${isPlayer2Start ? 'start-corner-p2' : ''}
                                 ${isPlaced ? 'placed' : ''}`}
                      style={cellStyle}
                      data-row={r}
                      data-col={c}
                      onClick={(e) => handleBoardClick(r, c, e)}
                      onDragOver={(e) => handleDragOver(e, r, c)}
                      onDrop={(e) => handleDrop(e, r, c)}
                      onMouseEnter={() => handleMouseEnter(r, c)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          <div className="controls">
            <button
              onClick={handleRotate}
              disabled={currentPlayer !== 1 || !selectedPiece || gameEnded || (pendingMove && !canRotatePendingMove())}
            >
              Rotate
            </button>
            <button
              onClick={handleFlip}
              disabled={currentPlayer !== 1 || !selectedPiece || gameEnded || (pendingMove && !canFlipPendingMove())}
            >
              Flip
            </button>
            <button
              onClick={handleUndo}
              className="undo-btn"
              disabled={!pendingMove || gameEnded}
            >
              Undo
            </button>
            <button
              onClick={handleConfirm}
              className="confirm-btn"
              disabled={!pendingMove || gameEnded}
            >
              Confirm
            </button>
            <button
              onClick={handlePass}
              className="pass-btn"
              disabled={currentPlayer !== 1 || gameEnded}
            >
              Pass
            </button>
          </div>

          {/* Floating piece that follows finger during touch drag */}
          {touchedPiece && touchPosition && selectedPiece && (
            <div
              className="floating-piece"
              style={{
                position: 'fixed',
                left: `${touchPosition.x}px`,
                top: `${touchPosition.y}px`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 9999,
                opacity: 0.8
              }}
            >
              {(() => {
                let displayShape = selectedPiece.shape;
                for (let i = 0; i < pieceTransform.rotation; i++) {
                  displayShape = rotatePiece(displayShape);
                }
                if (pieceTransform.flipped) {
                  displayShape = flipPiece(displayShape);
                }

                // Calculate bounding box
                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;
                displayShape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;

                return (
                  <div className="piece-grid" style={{ width: `${cols * 20}px` }}>
                    {Array.from({ length: rows }, (_, r) =>
                      Array.from({ length: cols }, (_, c) => {
                        const actualR = r + minRow;
                        const actualC = c + minCol;
                        const isFilled = displayShape[actualR]?.[actualC] === 1;
                        return (
                          <div
                            key={`${r}-${c}`}
                            className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                            style={{
                              backgroundColor: isFilled ? getColorStyles(playerColor).bg : 'transparent',
                              borderColor: isFilled ? getColorStyles(playerColor).border : 'rgba(0, 0, 0, 0.9)',
                              visibility: isFilled ? 'visible' : 'hidden',
                              width: '20px',
                              height: '20px'
                            }}
                          />
                        );
                      })
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="mobile-pieces-container">
            {mobileTab === 'your' ? (
              <div className="pieces-grid">
                {[...player1Pieces].sort((a, b) => {
                  // Sort used pieces to the end
                  if (a.used !== b.used) return a.used ? 1 : -1;
                  // Among unused pieces, sort by size (larger first)
                  const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                  const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                  return sizeB - sizeA;
                }).map(piece => {
                  const isSelected = selectedPiece?.id === piece.id;
                  let displayShape = piece.shape;

                  if (isSelected) {
                    for (let i = 0; i < pieceTransform.rotation; i++) {
                      displayShape = rotatePiece(displayShape);
                    }
                    if (pieceTransform.flipped) {
                      displayShape = flipPiece(displayShape);
                    }
                  }

                  // Calculate bounding box
                  let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                  displayShape.forEach((row, r) => {
                    row.forEach((cell, c) => {
                      if (cell === 1) {
                        minRow = Math.min(minRow, r);
                        maxRow = Math.max(maxRow, r);
                        minCol = Math.min(minCol, c);
                        maxCol = Math.max(maxCol, c);
                      }
                    });
                  });

                  const cols = maxCol - minCol + 1;
                  const rows = maxRow - minRow + 1;

                  const isTouched = touchedPiece?.id === piece.id;
                  const isPending = pendingMove && selectedPiece?.id === piece.id;

                  return (
                    <div
                      key={piece.id}
                      className={`piece ${piece.used ? 'used' : ''} ${isSelected ? 'selected' : ''} ${isPending ? 'pending' : ''}`}
                      onClick={() => handlePieceSelect(piece)}
                      draggable={!piece.used && currentPlayer === 1 && !gameEnded}
                      onDragStart={(e) => handleDragStart(e, piece)}
                      onDrag={handleDrag}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, piece)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <div className="piece-grid" style={{ width: viewMode === 'mobile' ? `${cols * 2.16}vw` : `${cols * 11}px`, opacity: isTouched ? 0 : 1 }}>
                        {Array.from({ length: rows }, (_, r) =>
                          Array.from({ length: cols }, (_, c) => {
                            const actualR = r + minRow;
                            const actualC = c + minCol;
                            const isFilled = displayShape[actualR]?.[actualC] === 1;
                            return (
                              <div
                                key={`${r}-${c}`}
                                className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                                style={{
                                  backgroundColor: isFilled && !isPending ? getColorStyles(playerColor).bg : 'transparent',
                                  borderColor: isFilled ? getColorStyles(playerColor).border : 'transparent',
                                  visibility: isFilled ? 'visible' : 'hidden'
                                }}
                              />
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="pieces-grid opponent-pieces">
                {[...player2Pieces].sort((a, b) => {
                  // Sort used pieces to the end
                  if (a.used !== b.used) return a.used ? 1 : -1;
                  // Among unused pieces, sort by size (larger first)
                  const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                  const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                  return sizeB - sizeA;
                }).map(piece => {
                  // Calculate bounding box
                  let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                  piece.shape.forEach((row, r) => {
                    row.forEach((cell, c) => {
                      if (cell === 1) {
                        minRow = Math.min(minRow, r);
                        maxRow = Math.max(maxRow, r);
                        minCol = Math.min(minCol, c);
                        maxCol = Math.max(maxCol, c);
                      }
                    });
                  });

                  const cols = maxCol - minCol + 1;
                  const rows = maxRow - minRow + 1;

                  return (
                    <div
                      key={piece.id}
                      className={`piece ${piece.used ? 'used' : ''}`}
                    >
                      <div className="piece-grid" style={{ width: viewMode === 'mobile' ? `${cols * 2.16}vw` : `${cols * 11}px` }}>
                        {Array.from({ length: rows }, (_, r) =>
                          Array.from({ length: cols }, (_, c) => {
                            const actualR = r + minRow;
                            const actualC = c + minCol;
                            const isFilled = piece.shape[actualR]?.[actualC] === 1;
                            return (
                              <div
                                key={`${r}-${c}`}
                                className={`piece-cell ${isFilled ? 'filled player2-piece' : ''}`}
                                style={{
                                  backgroundColor: isFilled ? getColorStyles(getOpponentColor(playerColor)).bg : 'transparent',
                                  borderColor: isFilled ? getColorStyles(getOpponentColor(playerColor)).border : 'rgba(0, 0, 0, 0.9)',
                                  visibility: isFilled ? 'visible' : 'hidden'
                                }}
                              />
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* No more legal moves message for mobile */}
          {mobileTab === 'your' && currentPlayer === 1 && !player1HasMoves && !gameEnded && (
            <div className="no-moves mobile-no-moves">No more legal moves</div>
          )}
          {mobileTab === 'opponent' && currentPlayer === 2 && !player2HasMoves && !gameEnded && (
            <div className="no-moves mobile-no-moves">No more legal moves</div>
          )}
        </div>

        {showQuitConfirm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Quit Game</h2>
              <p>Do you wish to quit the game?</p>
              <div className="modal-buttons">
                <button onClick={confirmQuit} className="modal-btn confirm-btn">
                  Yes, Quit
                </button>
                <button onClick={cancelQuit} className="modal-btn cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop view layout
  return (
    <div className="App">
      <div className="title-container">
        <h1>Block Rivals</h1>
        {!gameStartScreen && (
          <button
            onClick={handleReturnToMenu}
            className="menu-btn-title"
            title="Quit to Menu"
          >
            ✕
          </button>
        )}
      </div>

      <div className="game-container">
        <div className="side-panel">
          <div className={`player-info ${currentPlayer === 1 ? 'active' : ''}`}>
            <h3>{playerName}</h3>
            <div className="score-timer-row">
              <div className="score-section">
                <div className="score-label">Score</div>
                <div className="score-value">{score.player1}</div>
              </div>
              {currentPlayer === 1 && !gameEnded && (
                <div className="timer-section">
                  <div className="timer-text">{Math.max(0, timer)}s</div>
                  <div className="timer-bar">
                    <div className="timer-fill" style={{ width: `${(Math.max(0, timer) / timerDuration) * 100}%` }}></div>
                  </div>
                </div>
              )}
            </div>
            {currentPlayer === 1 && !player1HasMoves && !gameEnded && (
              <div className="no-moves">No more legal moves</div>
            )}
          </div>

          <div className="pieces-container">
            <h3>Your Pieces</h3>
            <div className="pieces-grid">
              {[...player1Pieces].sort((a, b) => {
                // Sort used pieces to the end
                if (a.used !== b.used) return a.used ? 1 : -1;
                // Among unused pieces, sort by size (larger first)
                const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                return sizeB - sizeA;
              }).map(piece => {
                const isSelected = selectedPiece?.id === piece.id;
                let displayShape = piece.shape;

                if (isSelected) {
                  for (let i = 0; i < pieceTransform.rotation; i++) {
                    displayShape = rotatePiece(displayShape);
                  }
                  if (pieceTransform.flipped) {
                    displayShape = flipPiece(displayShape);
                  }
                }

                // Calculate bounding box
                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                displayShape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;
                const isTouched = touchedPiece?.id === piece.id;
                const isPending = pendingMove && selectedPiece?.id === piece.id;

                return (
                  <div
                    key={piece.id}
                    className={`piece ${piece.used ? 'used' : ''} ${isSelected ? 'selected' : ''} ${isPending ? 'pending' : ''}`}
                    onClick={() => handlePieceSelect(piece)}
                    draggable={!piece.used && currentPlayer === 1 && !gameEnded}
                    onDragStart={(e) => handleDragStart(e, piece)}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, piece)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <div className="piece-grid" style={{ width: `${cols * 11}px`, opacity: isTouched ? 0 : 1 }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const actualR = r + minRow;
                          const actualC = c + minCol;
                          const isFilled = displayShape[actualR]?.[actualC] === 1;
                          return (
                            <div
                              key={`${r}-${c}`}
                              className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                              style={{
                                backgroundColor: isFilled && !isPending ? getColorStyles(playerColor).bg : 'transparent',
                                borderColor: isFilled ? getColorStyles(playerColor).border : 'transparent',
                                visibility: isFilled ? 'visible' : 'hidden'
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="board-container">
          <div className="game-over-placeholder">
            {gameEnded && (
              <div className="game-over">
                <h2>Game Over!</h2>
                <p>{score.player1 > score.player2 ? `${playerName} Wins!` : score.player2 > score.player1 ? `${botName} Wins!` : "It's a Tie!"}</p>
                <button onClick={handleNewGame} className="new-game-btn">New Game</button>
              </div>
            )}
          </div>

          <div className="board" style={{
            '--player1-color': getColorStyles(playerColor).bg,
            '--player1-border': getColorStyles(playerColor).border,
            '--player2-color': getColorStyles(getOpponentColor(playerColor)).bg,
            '--player2-border': getColorStyles(getOpponentColor(playerColor)).border
          }}>
            {boardDisplay.map((row, r) => (
              <div key={r} className="board-row">
                {row.map((cell, c) => {
                  const isLegal = !pendingMove && legalPositions.some(pos => pos.row === r && pos.col === c);
                  const isPlayer1Start = r === 4 && c === 4;
                  const isPlayer2Start = r === 9 && c === 9;
                  const isPlaced = placedCells.has(`${r},${c}`);

                  const player1Styles = cell === 1 ? getColorStyles(playerColor) : {};
                  const player2Styles = cell === 2 ? getColorStyles(getOpponentColor(playerColor)) : {};
                  const cellStyle = cell === 1 ? {
                    backgroundColor: player1Styles.bg,
                    borderColor: player1Styles.border
                  } : cell === 2 ? {
                    backgroundColor: player2Styles.bg,
                    borderColor: player2Styles.border
                  } : {};

                  return (
                    <div
                      key={c}
                      className={`cell ${cell === 1 ? 'player1' : cell === 2 ? 'player2' : ''}
                                 ${cell === 'preview' ? 'preview' : ''}
                                 ${isLegal ? 'legal' : ''}
                                 ${isPlayer1Start ? 'start-corner-p1' : ''}
                                 ${isPlayer2Start ? 'start-corner-p2' : ''}
                                 ${isPlaced ? 'placed' : ''}`}
                      style={cellStyle}
                      data-row={r}
                      data-col={c}
                      onClick={(e) => handleBoardClick(r, c, e)}
                      onDragOver={(e) => handleDragOver(e, r, c)}
                      onDrop={(e) => handleDrop(e, r, c)}
                      onMouseEnter={() => handleMouseEnter(r, c)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          <div className="controls">
            <button
              onClick={handleRotate}
              disabled={currentPlayer !== 1 || !selectedPiece || gameEnded || (pendingMove && !canRotatePendingMove())}
            >
              Rotate
            </button>
            <button
              onClick={handleFlip}
              disabled={currentPlayer !== 1 || !selectedPiece || gameEnded || (pendingMove && !canFlipPendingMove())}
            >
              Flip
            </button>
            <button
              onClick={handleUndo}
              className="undo-btn"
              disabled={!pendingMove || gameEnded}
            >
              Undo
            </button>
            <button
              onClick={handleConfirm}
              className="confirm-btn"
              disabled={!pendingMove || gameEnded}
            >
              Confirm
            </button>
            <button
              onClick={handlePass}
              className="pass-btn"
              disabled={currentPlayer !== 1 || gameEnded}
            >
              Pass
            </button>
          </div>
        </div>

        <div className="side-panel">
          <div className={`player-info ${currentPlayer === 2 ? 'active' : ''}`}>
            <h3>{botName}</h3>
            <div className="score-timer-row">
              <div className="score-section">
                <div className="score-label">Score</div>
                <div className="score-value">{score.player2}</div>
              </div>
              {currentPlayer === 2 && !gameEnded && (
                <div className="timer-section">
                  <div className="timer-text">{Math.max(0, timer)}s</div>
                  <div className="timer-bar">
                    <div className="timer-fill" style={{ width: `${(Math.max(0, timer) / timerDuration) * 100}%` }}></div>
                  </div>
                </div>
              )}
            </div>
            {currentPlayer === 2 && !player2HasMoves && !gameEnded && (
              <div className="no-moves">No more legal moves</div>
            )}
          </div>

          <div className="pieces-container opponent-pieces">
            <h3>Opponent Pieces</h3>
            <div className="pieces-grid">
              {[...player2Pieces].sort((a, b) => {
                // Sort used pieces to the end
                if (a.used !== b.used) return a.used ? 1 : -1;
                // Among unused pieces, sort by size (larger first)
                const sizeA = a.shape.flat().filter(cell => cell === 1).length;
                const sizeB = b.shape.flat().filter(cell => cell === 1).length;
                return sizeB - sizeA;
              }).map(piece => {
                // Calculate bounding box
                let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;

                piece.shape.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell === 1) {
                      minRow = Math.min(minRow, r);
                      maxRow = Math.max(maxRow, r);
                      minCol = Math.min(minCol, c);
                      maxCol = Math.max(maxCol, c);
                    }
                  });
                });

                const cols = maxCol - minCol + 1;
                const rows = maxRow - minRow + 1;

                return (
                  <div
                    key={piece.id}
                    className={`piece ${piece.used ? 'used' : ''}`}
                  >
                    <div className="piece-grid" style={{ width: `${cols * 11}px` }}>
                      {Array.from({ length: rows }, (_, r) =>
                        Array.from({ length: cols }, (_, c) => {
                          const actualR = r + minRow;
                          const actualC = c + minCol;
                          const isFilled = piece.shape[actualR]?.[actualC] === 1;
                          return (
                            <div
                              key={`${r}-${c}`}
                              className={`piece-cell ${isFilled ? 'filled player2-piece' : ''}`}
                              style={{
                                backgroundColor: isFilled ? getColorStyles(getOpponentColor(playerColor)).bg : 'transparent',
                                borderColor: isFilled ? getColorStyles(getOpponentColor(playerColor)).border : 'rgba(0, 0, 0, 0.9)',
                                visibility: isFilled ? 'visible' : 'hidden'
                              }}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Floating piece that follows finger during touch drag */}
      {touchedPiece && touchPosition && selectedPiece && (
        <div
          className="floating-piece"
          style={{
            position: 'fixed',
            left: `${touchPosition.x}px`,
            top: `${touchPosition.y}px`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 9999,
            opacity: 0.8
          }}
        >
          {(() => {
            let displayShape = selectedPiece.shape;
            for (let i = 0; i < pieceTransform.rotation; i++) {
              displayShape = rotatePiece(displayShape);
            }
            if (pieceTransform.flipped) {
              displayShape = flipPiece(displayShape);
            }

            // Calculate bounding box
            let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;
            displayShape.forEach((row, r) => {
              row.forEach((cell, c) => {
                if (cell === 1) {
                  minRow = Math.min(minRow, r);
                  maxRow = Math.max(maxRow, r);
                  minCol = Math.min(minCol, c);
                  maxCol = Math.max(maxCol, c);
                }
              });
            });

            const cols = maxCol - minCol + 1;
            const rows = maxRow - minRow + 1;

            return (
              <div className="piece-grid" style={{ width: `${cols * 20}px` }}>
                {Array.from({ length: rows }, (_, r) =>
                  Array.from({ length: cols }, (_, c) => {
                    const actualR = r + minRow;
                    const actualC = c + minCol;
                    const isFilled = displayShape[actualR]?.[actualC] === 1;
                    return (
                      <div
                        key={`${r}-${c}`}
                        className={`piece-cell ${isFilled ? 'filled player1-piece' : ''}`}
                        style={{
                          backgroundColor: isFilled ? getColorStyles(playerColor).bg : 'transparent',
                          borderColor: isFilled ? getColorStyles(playerColor).border : 'rgba(0, 0, 0, 0.9)',
                          visibility: isFilled ? 'visible' : 'hidden',
                          width: '20px',
                          height: '20px'
                        }}
                      />
                    );
                  })
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showQuitConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Quit Game</h2>
            <p>Do you wish to quit the game?</p>
            <div className="modal-buttons">
              <button onClick={confirmQuit} className="modal-btn confirm-btn">
                Yes, Quit
              </button>
              <button onClick={cancelQuit} className="modal-btn cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
